/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2022-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// External/Third-party dependencies
import React from 'react';

// Internal dependencies
import useLocateService from '@rakuten/hooks/useLocateService';


// -----------------------------------------------------------------------------
// PUSH TO CLOUD
// -----------------------------------------------------------------------------

//flag to prevent multiple calls while re-rendering
let inProgress = false;

/**
 * Valid push2cloud states
 */
const pushToCloudStates = {
  DEFAULT: "default", //nothing to push
  PUSHING: "pushing", //currently pushing to cloud
  FAIL: "fail", //failed to push something
  SUCCESS: "success" //successfully pushed something
};

/**
 * Call backend with stored publicationId to store a sample in the cloud
 */
export default function usePushToCloud() {
  const [localConfig, pushToCloud, sessionStorage] =
    useLocateService(['localConfig', 'pushToCloud', 'sessionStorage']);
  const [pushToCloudState, setCurrentState] = React.useState(pushToCloudStates.DEFAULT);

  /**
   * Reset function for pushToCloudState. Needs to be useCallback to prevent
   * multiple react render cycles
   */
  const resetPushToCloudState = React.useCallback(
    () => setCurrentState(pushToCloudStates.DEFAULT),
    [ setCurrentState ]
  );

  /**
   * Effect to call backend
   */
  React.useEffect(function pushToCloudEffectHook() {

    if (localConfig && sessionStorage && pushToCloud) {

      //prevent multiple calls while re-rendering
      if (!inProgress) {
        inProgress = true;

        const appConfig = localConfig.getLocalConfig();

        //if a pubid is stored in sessionstorage we try to push it
        const pubId = sessionStorage
          .getItem(appConfig.CONSTANTS.PUSH_TO_CLOUD.KEY_P2C_ID);

        if (pubId) {
          setCurrentState(pushToCloudStates.PUSHING);

          //remove the pubid to prevent sending it multiple times
          sessionStorage.removeItem(appConfig.CONSTANTS.PUSH_TO_CLOUD.KEY_P2C_ID);

          //call service
          pushToCloud.pushPublication(pubId)
            .then(() => setCurrentState(pushToCloudStates.SUCCESS))
            .catch(() => pushToCloudStates.FAIL)
            .finally(() => inProgress = false);
        } else {
          inProgress = false
        }
      }
    }

  }, [localConfig, pushToCloud, sessionStorage]);

  return [pushToCloudState, pushToCloudStates, resetPushToCloudState];
}
